import { Box, Button, Icon, Link, Stack, Text, useDisclosure } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MdCookie } from 'react-icons/md';
import RouteEnum from '../../routing/routes';
import { AnalyticsConsentService } from '../../services/analytics/analytics-consent.service';
import LocalizedLink from '../common/localized-link';
import { AnalyticsConsentOption } from '../../services/analytics/types';
import { useAnalyticsConsent } from '../../hooks/use-analytics-consent';
import GradientButton from '../common/buttons/gradient-button';

type CookiesBannerProps = {
    onHide: () => void;
};

const CookiesBanner: React.FC<CookiesBannerProps> = ({ onHide }) => {
    const bannerDisclosure = useDisclosure();
    const { t } = useTranslation();
    const { analyticsConsentRequired } = useAnalyticsConsent();

    useEffect(() => {
        if (!analyticsConsentRequired) {
            onHide();
            return;
        }

        if (!bannerDisclosure.isOpen) {
            if (!AnalyticsConsentService.getConsent()) {
                bannerDisclosure.onOpen();
            } else {
                onHide();
            }
        }
    }, [analyticsConsentRequired, bannerDisclosure.isOpen]);

    const setConsent = (consent: AnalyticsConsentOption) => {
        AnalyticsConsentService.setConsent(consent);
        bannerDisclosure.onClose();
        onHide();
    };

    return (
        <>
            {bannerDisclosure.isOpen && (
                <Box pos="fixed" left={{ base: 3, md: 6 }} right={{ base: 3, md: 6 }} bottom={{ base: 3, md: 6 }} zIndex={2}>
                    <Stack
                        direction={{ base: 'column', md: 'row' }}
                        spacing={{ base: 4, md: 6 }}
                        align={{ base: 'stretch', md: 'center' }}
                        bg="nobleDarkGray.500"
                        border="1px solid"
                        borderColor="whiteAlpha.200"
                        boxShadow="0 18px 50px rgba(0, 0, 0, 0.28)"
                        borderRadius="2xl"
                        px={{ base: 4, md: 6 }}
                        py={{ base: 4, md: 4 }}
                        backdropFilter="blur(12px)"
                    >
                        <Stack direction="row" spacing={3} align="flex-start" flex="1">
                            <Icon as={MdCookie} color="background" boxSize={8} mt={1} flexShrink={0} />
                            <Stack spacing={1} flex="1">
                                <Text fontWeight="bold" textAlign="left" color="background" fontSize={{ base: 'md', md: 'lg' }}>
                                    {t('Cookies')}
                                </Text>
                                <Text textAlign="left" color="background" fontSize={{ base: 'sm', md: 'md' }} lineHeight="tall">
                                    {t('usesCookies')}{' '}
                                    <Link fontWeight="bold" as={LocalizedLink} to={`${RouteEnum.privacyPolicy}/`}>
                                        {t('Privacy Policy')}
                                    </Link>{' '}
                                    {t('forDetails')}
                                </Text>
                            </Stack>
                        </Stack>
                        <Stack direction={{ base: 'column-reverse', sm: 'row' }} spacing={3} w={{ base: 'full', md: 'auto' }}>
                            <Button colorScheme="gray" onClick={() => setConsent('rejected')} w={{ base: 'full', sm: 'auto' }}>
                                {t('Reject analytics')}
                            </Button>
                            <GradientButton onClick={() => setConsent('accepted')} w={{ base: 'full', sm: 'auto' }}>
                                {t('Accept analytics')}
                            </GradientButton>
                        </Stack>
                    </Stack>
                </Box>
            )}
        </>
    );
};

export default CookiesBanner;
